export type NodesList = {
  node: Node;
  start: number;
  end: number;
}[];

/**
 * Построение списка текстовых нод внутри элемента, с указанием их позиций в общем тексте.
 * Это нужно для того, чтобы потом по найденным позициям термов в тексте находить соответствующие диапазоны в DOM и выделять их.
 * Возвращает также сам общий текст: между нодами с разными родителями вставляется
 * разделитель, чтобы граница считалась границей слова, иначе первое слово
 * следующего элемента списка приклеивается к последнему слову предыдущего, и
 * матчеры, привязанные к началу слова, находят только совпадение в первом блоке.
 */
export function buildNodesList(root: Node): {
  nodesList: NodesList;
  text: string;
} {
  const textIndex: NodesList = [];
  let text = '';
  if (!root) {
    return {
      nodesList: textIndex,
      text
    };
  }

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT
  );

  // A non-word character so findTextRanges treats the boundary as a word start.
  const SEPARATOR = '\n';
  let offset = 0;
  let prevParent: Element | null = null;
  let node = walker.nextNode();

  while (node) {
    const length = node.nodeValue?.length ?? 0;
    if (length > 0) {
      const parent = node.parentElement;
      if (textIndex.length > 0 && parent !== prevParent) {
        text += SEPARATOR;
        offset += SEPARATOR.length;
      }
      textIndex.push({
        node,
        start: offset,
        end: offset + length
      });
      text += node.nodeValue ?? '';
      offset += length;
      prevParent = parent;
    }
    node = walker.nextNode();
  }

  return {
    nodesList: textIndex,
    text
  };
}

/**
 * Схлопывает прилегающие и вложенные диапазоны в один, чтобы не было пересечений при выделении
 */
function collapseRanges(ranges: Array<[number, number]>) {
  if (ranges.length === 0) {
    return [];
  }

  // т.к. вложенные термы мы сжимаем раньше, можно было бы обойтись только сортировкой,
  // но пусть пока будет на случай если есть прилегающие диапазоны
  const sorted = [...ranges].sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const result: Array<[number, number]> = [];

  let [curStart, curEnd] = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];

    if (s > curEnd) {
      result.push([curStart, curEnd]);
      curStart = s;
      curEnd = e;
    } else {
      curEnd = Math.max(curEnd, e);
    }
  }
  result.push([curStart, curEnd]);
  return result;
}

export function prepareString(termsOrText: string) {
  return termsOrText
    .replaceAll(' AND ', ' ')
    .replaceAll(' NOT ', ' ')
    .replaceAll(' OR ', ' ')
    .toLowerCase()
    .replaceAll('ё', 'е');
}

export function tokenizeString(q: string) {
  const result: string[] = [];
  const tokenRegex = /"([^"]*)"|([^\s,—"()|]+)/g;
  for (let m = tokenRegex.exec(q); m !== null; m = tokenRegex.exec(q)) {
    const token = m[1] !== undefined ? m[1] : m[2];
    if (token) {
      result.push(token);
    }
  }
  return result;
}

/**
 * Находит все вхождения термов в тексте и возвращает их позиции
 */
export function findTextRanges(text: string, terms: string[]) {
  const result: [number, number][] = [];

  for (const term of terms) {
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(^|[^\\p{L}\\p{N}_])(${escapedTerm})`, 'gu');

    for (let match = re.exec(text); match !== null; match = re.exec(text)) {
      const prefix = match[1] ?? '';
      const matchedTerm = match[2] ?? '';
      const start = match.index + prefix.length;
      result.push([start, start + matchedTerm.length]);
    }
  }

  return collapseRanges(result);
}

/**
 * Проверяет, что каждый терм является префиксом какого-то слова в тексте.
 */
export function matchesAllTerms(text: string, terms: string[]): boolean {
  if (terms.length === 0) {
    return true;
  }
  const matched = new Set<string>();
  const textTerms = tokenizeString(prepareString(text));
  for (let i = 0; i < textTerms.length; i += 1) {
    const word = textTerms[i];
    for (const term of terms) {
      if (word.startsWith(term)) {
        matched.add(term);
      }
    }
  }
  return matched.size === terms.length;
}

/**
 * Преобразует позиции термов в тексте в диапазоны DOM для выделения
 */
export function findDomRanges(textRanges: [number, number][], index: NodesList) {
  let nodeIndex = 0;
  const domRanges: Range[] = [];

  for (const [start, end] of textRanges) {
    while (
      nodeIndex < index.length
      && index[nodeIndex].end <= start
    ) {
      nodeIndex++;
    }

    let i = nodeIndex;

    while (i < index.length && index[i].start < end) {
      const entry = index[i];

      const rangeStart = Math.max(start, entry.start);
      const rangeEnd = Math.min(end, entry.end);

      if (rangeStart < rangeEnd) {
        const r = new Range();
        r.setStart(entry.node, rangeStart - entry.start);
        r.setEnd(entry.node, rangeEnd - entry.start);
        domRanges.push(r);
      }

      i++;
    }
  }

  return domRanges;
}
